import { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function FieldLabel({ label, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}
      </MDTypography>
      {children}
    </MDBox>
  );
}

function Section({ title, icon, children }) {
  return (
    <MDBox mb={4}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 16 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 3 }} />
      {children}
    </MDBox>
  );
}

// Champ non modifiable (ex. email d'un enseignant, que seul un admin peut changer)
const fieldStyleLectureSeule = {
  ...fieldStyle, background: "#f0f2f5", color: "#7b809a", cursor: "not-allowed",
};

function Profil() {
  const { user } = useAuth();
  const emailModifiable = user?.role === "admin";
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [stream, setStream] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const [profil, setProfil] = useState({
    prenom: "", nom: "", email: "", telephone: "", ville: "",
  });

  const [motDePasse, setMotDePasse] = useState({
    ancien: "", nouveau: "", confirmation: "",
  });

  const [savingProfil, setSavingProfil] = useState(false);
  const [savingMdp, setSavingMdp] = useState(false);
  const [successProfil, setSuccessProfil] = useState("");
  const [successMdp, setSuccessMdp] = useState("");
  const [errorProfil, setErrorProfil] = useState("");
  const [errorMdp, setErrorMdp] = useState("");

  // 2FA
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [passwordDisable, setPasswordDisable] = useState("");
  const [loading2fa, setLoading2fa] = useState(false);
  const [success2fa, setSuccess2fa] = useState("");
  const [error2fa, setError2fa] = useState("");

  useEffect(() => {
    if (user) {
      api.get(`/enseignants/${user.id}`).then(({ data }) => {
        setProfil({
          prenom: data.prenom || "",
          nom: data.nom || "",
          email: data.email || "",
          telephone: data.telephone || "",
          ville: data.ville || "",
        });
        if (data.photo) setPhotoPreview(data.photo);
      }).catch(() => {
        api.get(`/users/${user.id}`).then(({ data }) => {
          setProfil({
            prenom: data.prenom || "",
            nom: data.nom || "",
            email: data.email || "",
            telephone: data.telephone || "",
            ville: data.ville || "",
          });
          if (data.photo) setPhotoPreview(data.photo);
        });
      });

      // Charger le statut 2FA
      api.get("/auth/2fa/status").then(({ data }) => {
        setTwoFactorEnabled(data.two_factor_enabled);
      }).catch(() => {});
    }
  }, [user]);

  const startWebcam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      setShowWebcam(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch { alert("Impossible d'accéder à la webcam."); }
  };

  const stopWebcam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setShowWebcam(false);
    setStream(null);
  };

  const takePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      setPhotoFile(file);
      setPhotoPreview(canvas.toDataURL("image/jpeg"));
    }, "image/jpeg");
    stopWebcam();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const handleSaveProfil = async () => {
    setSavingProfil(true);
    setErrorProfil("");
    setSuccessProfil("");
    try {
      const endpoint = user.role === "enseignant"
        ? `/enseignants/${user.id}`
        : `/users/${user.id}`;
      await api.put(endpoint, profil);
      if (photoFile) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        const photoEndpoint = user.role === "enseignant"
          ? `/enseignants/${user.id}/photo`
          : `/users/${user.id}/photo`;
        await api.post(photoEndpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setSuccessProfil("✅ Profil mis à jour avec succès !");
      setPhotoFile(null);
    } catch (err) {
      setErrorProfil(err.response?.data?.message || "Erreur lors de la mise à jour");
    } finally {
      setSavingProfil(false);
    }
  };

  const handleSaveMdp = async () => {
    setErrorMdp("");
    setSuccessMdp("");
    if (!motDePasse.ancien || !motDePasse.nouveau || !motDePasse.confirmation) {
      setErrorMdp("Tous les champs sont obligatoires");
      return;
    }
    if (motDePasse.nouveau !== motDePasse.confirmation) {
      setErrorMdp("Les mots de passe ne correspondent pas");
      return;
    }
    if (motDePasse.nouveau.length < 8) {
      setErrorMdp("Le nouveau mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setSavingMdp(true);
    try {
      await api.put(`/auth/change-password`, {
        ancien_mot_de_passe: motDePasse.ancien,
        nouveau_mot_de_passe: motDePasse.nouveau,
      });
      setSuccessMdp("✅ Mot de passe modifié avec succès !");
      setMotDePasse({ ancien: "", nouveau: "", confirmation: "" });
    } catch (err) {
      setErrorMdp(err.response?.data?.message || "Erreur lors du changement de mot de passe");
    } finally {
      setSavingMdp(false);
    }
  };

  // 2FA — Générer le QR code
  const handleSetup2FA = async () => {
    setLoading2fa(true);
    setError2fa("");
    setSuccess2fa("");
    try {
      const { data } = await api.get("/auth/2fa/setup");
      setTwoFactorSetup(data);
      setTotpCode("");
    } catch (err) {
      setError2fa("Erreur lors de la génération du QR code");
    } finally {
      setLoading2fa(false);
    }
  };

  // 2FA — Activer après scan
  const handleEnable2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError2fa("Entrez le code à 6 chiffres de votre application");
      return;
    }
    setLoading2fa(true);
    setError2fa("");
    try {
      await api.post("/auth/2fa/enable", { totp_code: totpCode });
      setTwoFactorEnabled(true);
      setTwoFactorSetup(null);
      setTotpCode("");
      setSuccess2fa("✅ Double authentification activée avec succès !");
    } catch (err) {
      setError2fa(err.response?.data?.message || "Code invalide");
    } finally {
      setLoading2fa(false);
    }
  };

  // 2FA — Désactiver
  const handleDisable2FA = async () => {
    if (!passwordDisable) {
      setError2fa("Entrez votre mot de passe pour désactiver le 2FA");
      return;
    }
    setLoading2fa(true);
    setError2fa("");
    try {
      await api.post("/auth/2fa/disable", { password: passwordDisable });
      setTwoFactorEnabled(false);
      setPasswordDisable("");
      setSuccess2fa("✅ Double authentification désactivée");
    } catch (err) {
      setError2fa(err.response?.data?.message || "Erreur");
    } finally {
      setLoading2fa(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(106,27,154,0.4)",
                display: "flex", alignItems: "center", gap: 3,
              }}>
                <MDBox sx={{
                  width: 80, height: 80, borderRadius: "50%",
                  border: "3px solid rgba(255,255,255,0.5)",
                  overflow: "hidden", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(255,255,255,0.2)",
                }}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="profil"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Icon style={{ fontSize: 40, color: "rgba(255,255,255,0.8)" }}>person</Icon>
                  )}
                </MDBox>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    {user?.prenom} {user?.nom}
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    {user?.role === "admin" ? "👑 Administrateur" : "👨‍🏫 Enseignant"}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>

                {/* ── PHOTO ── */}
                <Section title="Photo de profil" icon="camera_alt">
                  <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
                    <MDBox sx={{
                      width: 100, height: 100, borderRadius: "50%",
                      border: "3px solid #f3e5f5", overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "#f5f7ff", flexShrink: 0,
                    }}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="aperçu"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Icon style={{ fontSize: 50, color: "#e1bee7" }}>person</Icon>
                      )}
                    </MDBox>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <MDButton variant="outlined" color="secondary" size="small"
                        onClick={startWebcam}>
                        <Icon>camera_alt</Icon>&nbsp; Webcam
                      </MDButton>
                      <MDButton variant="outlined" color="secondary" size="small"
                        component="label">
                        <Icon>upload</Icon>&nbsp; Uploader
                        <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                      </MDButton>
                      {photoPreview && (
                        <MDButton variant="outlined" color="error" size="small"
                          onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}>
                          <Icon>cancel</Icon>&nbsp; Supprimer
                        </MDButton>
                      )}
                    </MDBox>
                  </MDBox>
                  {showWebcam && (
                    <MDBox mt={2}>
                      <video ref={videoRef} autoPlay
                        style={{ width: "100%", maxWidth: 400, borderRadius: 10 }} />
                      <canvas ref={canvasRef} style={{ display: "none" }} />
                      <MDBox display="flex" gap={2} mt={1}>
                        <MDButton variant="gradient" color="success" size="small"
                          onClick={takePhoto}>
                          <Icon>camera</Icon>&nbsp; Prendre la photo
                        </MDButton>
                        <MDButton variant="outlined" color="error" size="small"
                          onClick={stopWebcam}>
                          Annuler
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}
                </Section>

                {/* ── INFORMATIONS PERSONNELLES ── */}
                <Section title="Informations personnelles" icon="person">
                  {errorProfil && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
                    }}>
                      <MDTypography variant="caption" color="error" fontWeight="medium">
                        ⚠️ {errorProfil}
                      </MDTypography>
                    </MDBox>
                  )}
                  {successProfil && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#e8f5e9", borderRadius: "10px", border: "1px solid #a5d6a7",
                    }}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#2e7d32" }}>
                        {successProfil}
                      </MDTypography>
                    </MDBox>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Prénom">
                        <input value={profil.prenom}
                          onChange={(e) => setProfil((p) => ({ ...p, prenom: e.target.value }))}
                          style={fieldStyle} autoComplete="off"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Nom">
                        <input value={profil.nom}
                          onChange={(e) => setProfil((p) => ({ ...p, nom: e.target.value }))}
                          style={fieldStyle} autoComplete="off"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Email">
                        {emailModifiable ? (
                          <input value={profil.email}
                            onChange={(e) => setProfil((p) => ({ ...p, email: e.target.value }))}
                            style={fieldStyle} autoComplete="off"
                            onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                        ) : (
                          <input value={profil.email} readOnly disabled
                            style={fieldStyleLectureSeule}
                            title="Seul un administrateur peut modifier l'email" />
                        )}
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Téléphone">
                        <input value={profil.telephone}
                          onChange={(e) => setProfil((p) => ({ ...p, telephone: e.target.value }))}
                          style={fieldStyle} autoComplete="off"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Ville">
                        <input value={profil.ville}
                          onChange={(e) => setProfil((p) => ({ ...p, ville: e.target.value }))}
                          style={fieldStyle} autoComplete="off"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                  </Grid>
                  <MDBox display="flex" justifyContent="flex-end" mt={1}>
                    <MDButton disabled={savingProfil} onClick={handleSaveProfil} sx={{
                      px: 4, background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#4a148c" },
                    }}>
                      {savingProfil ? "Enregistrement..." : "Sauvegarder le profil"}
                    </MDButton>
                  </MDBox>
                </Section>

                {/* ── MOT DE PASSE ── */}
                <Section title="Changer le mot de passe" icon="lock">
                  {errorMdp && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
                    }}>
                      <MDTypography variant="caption" color="error" fontWeight="medium">
                        ⚠️ {errorMdp}
                      </MDTypography>
                    </MDBox>
                  )}
                  {successMdp && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#e8f5e9", borderRadius: "10px", border: "1px solid #a5d6a7",
                    }}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#2e7d32" }}>
                        {successMdp}
                      </MDTypography>
                    </MDBox>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FieldLabel label="Mot de passe actuel">
                        <input type="password" value={motDePasse.ancien}
                          onChange={(e) => setMotDePasse((p) => ({ ...p, ancien: e.target.value }))}
                          style={fieldStyle} autoComplete="new-password"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Nouveau mot de passe">
                        <input type="password" value={motDePasse.nouveau}
                          onChange={(e) => setMotDePasse((p) => ({ ...p, nouveau: e.target.value }))}
                          style={fieldStyle} autoComplete="new-password"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel label="Confirmer le nouveau mot de passe">
                        <input type="password" value={motDePasse.confirmation}
                          onChange={(e) => setMotDePasse((p) => ({ ...p, confirmation: e.target.value }))}
                          style={fieldStyle} autoComplete="new-password"
                          onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                      </FieldLabel>
                    </Grid>
                  </Grid>
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="text">
                      Le mot de passe doit contenir au moins 8 caractères.
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" justifyContent="flex-end" mt={2}>
                    <MDButton disabled={savingMdp} onClick={handleSaveMdp} sx={{
                      px: 4, background: "linear-gradient(135deg, #c62828, #e53935)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#b71c1c" },
                    }}>
                      {savingMdp ? "Modification..." : "Changer le mot de passe"}
                    </MDButton>
                  </MDBox>
                </Section>

                {/* ── DOUBLE AUTHENTIFICATION ── */}
                <Section title="Double authentification (2FA)" icon="security">
                  {error2fa && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
                    }}>
                      <MDTypography variant="caption" color="error" fontWeight="medium">
                        ⚠️ {error2fa}
                      </MDTypography>
                    </MDBox>
                  )}
                  {success2fa && (
                    <MDBox mb={2} p={2} sx={{
                      background: "#e8f5e9", borderRadius: "10px", border: "1px solid #a5d6a7",
                    }}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#2e7d32" }}>
                        {success2fa}
                      </MDTypography>
                    </MDBox>
                  )}

                  {/* Statut actuel */}
                  <MDBox display="flex" alignItems="center" gap={2} mb={3} p={2} sx={{
                    background: twoFactorEnabled ? "#e8f5e9" : "#fff8e1",
                    borderRadius: "12px",
                    border: `1px solid ${twoFactorEnabled ? "#a5d6a7" : "#ffe082"}`,
                  }}>
                    <Icon sx={{
                      fontSize: 32,
                      color: twoFactorEnabled ? "#2e7d32" : "#f57f17",
                    }}>
                      {twoFactorEnabled ? "verified_user" : "gpp_maybe"}
                    </Icon>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold" sx={{
                        color: twoFactorEnabled ? "#2e7d32" : "#f57f17",
                      }}>
                        {twoFactorEnabled ? "2FA activé" : "2FA désactivé"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {twoFactorEnabled
                          ? "Votre compte est protégé par la double authentification"
                          : "Activez le 2FA pour sécuriser davantage votre compte"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* 2FA désactivé — proposer d'activer */}
                  {!twoFactorEnabled && !twoFactorSetup && (
                    <MDBox>
                      <MDTypography variant="body2" color="text" mb={2}>
                        La double authentification ajoute une couche de sécurité supplémentaire.
                        Après activation, vous devrez saisir un code généré par votre application
                        (Google Authenticator, Authy...) à chaque connexion.
                      </MDTypography>
                      <MDButton onClick={handleSetup2FA} disabled={loading2fa} sx={{
                        background: "linear-gradient(135deg, #1565c0, #1976d2)",
                        color: "#fff", borderRadius: "8px", fontWeight: 700,
                        "&:hover": { background: "#0d47a1" },
                      }}>
                        <Icon sx={{ mr: 0.5 }}>qr_code</Icon>
                        {loading2fa ? "Génération..." : "Configurer le 2FA"}
                      </MDButton>
                    </MDBox>
                  )}

                  {/* QR Code affiché */}
                  {!twoFactorEnabled && twoFactorSetup && (
                    <MDBox>
                      <MDTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                        📱 Étape 1 — Scannez ce QR code avec Google Authenticator ou Authy
                      </MDTypography>
                      <MDBox display="flex" justifyContent="center" mb={2}>
                        <img src={twoFactorSetup.qrCode} alt="QR Code 2FA"
                          style={{ width: 200, height: 200, borderRadius: 12,
                            border: "2px solid #e0e0e0" }} />
                      </MDBox>
                      <MDBox mb={2} p={1.5} sx={{
                        background: "#f8faff", borderRadius: "8px", border: "1px solid #e3f2fd",
                      }}>
                        <MDTypography variant="caption" color="text">
                          Si vous ne pouvez pas scanner, entrez ce code manuellement :
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="bold" display="block"
                          sx={{ color: "#1565c0", fontSize: 13, letterSpacing: 2 }}>
                          {twoFactorSetup.secret}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                        🔢 Étape 2 — Entrez le code généré par l&apos;application
                      </MDTypography>
                      <MDBox display="flex" gap={2} alignItems="flex-end">
                        <MDBox flex={1}>
                          <input
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            style={{
                              ...fieldStyle,
                              fontSize: "24px", textAlign: "center",
                              letterSpacing: "8px", fontWeight: 700,
                            }}
                            onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </MDBox>
                        <MDButton onClick={handleEnable2FA} disabled={loading2fa || totpCode.length !== 6}
                          sx={{
                            background: "linear-gradient(135deg, #2e7d32, #388e3c)",
                            color: "#fff", borderRadius: "8px", fontWeight: 700, px: 3,
                            "&:hover": { background: "#1b5e20" },
                          }}>
                          {loading2fa ? "Vérification..." : "Activer"}
                        </MDButton>
                        <MDButton variant="outlined" color="secondary"
                          onClick={() => { setTwoFactorSetup(null); setTotpCode(""); setError2fa(""); }}>
                          Annuler
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}

                  {/* 2FA activé — proposer de désactiver */}
                  {twoFactorEnabled && (
                    <MDBox>
                      <MDTypography variant="body2" color="text" mb={2}>
                        Pour désactiver le 2FA, confirmez votre mot de passe.
                      </MDTypography>
                      <MDBox display="flex" gap={2} alignItems="flex-end">
                        <MDBox flex={1}>
                          <FieldLabel label="Mot de passe">
                            <input type="password" value={passwordDisable}
                              onChange={(e) => setPasswordDisable(e.target.value)}
                              placeholder="Votre mot de passe"
                              style={fieldStyle}
                              onFocus={(e) => (e.target.style.borderColor = "#c62828")}
                              onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                          </FieldLabel>
                        </MDBox>
                        <MDButton onClick={handleDisable2FA} disabled={loading2fa} sx={{
                          background: "linear-gradient(135deg, #c62828, #e53935)",
                          color: "#fff", borderRadius: "8px", fontWeight: 700, px: 3,
                          mb: 2, "&:hover": { background: "#b71c1c" },
                        }}>
                          {loading2fa ? "Désactivation..." : "Désactiver le 2FA"}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}
                </Section>

              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Profil;
